/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import YouTubePlayerFactory from 'youtube-player';

import {Player} from '@amblique/base/app/plugins/video/adapters/player';
import {YoutubePlayer} from '@amblique/base/app/plugins/video/adapters/youtube-player';
import {AbstractVideo} from '@amblique/base/app/plugins/video/providers/abstract-video';

/**
 * Youtube model
 *
 * @typedef {import('@amblique/base/app/plugins/video/models/metadata').Metadata} Metadata
 */
export class Youtube extends AbstractVideo {
  static TYPE = 'youtube';
  static PLAYER = YoutubePlayer;
  static PARAMS = ['autoplay', 'cc_lang_pref', 'cc_load_policy', 'color', 'controls', 'disablekb', 'enablejsapi', 'end', 'fs', 'hl', 'iv_load_policy', 'list', 'listType', 'loop', 'mute', 'modestbranding', 'origin', 'playlist', 'playsinline', 'rel', 'start', 'widget_referrer'];
  static EVENTS = ['onReady', 'onStateChange', 'onPlaybackQualityChange', 'onPlaybackRateChange', 'onError', 'onApiChange'];
  static ID_PATTERN = /(?:^|\/|=|:)([^"&?/\s=#.:]{11})(?:\?|&|\/|"|,|$)/;
  static TYPE_PATTERN = /youtube|youtu\.be|y2u\.be|i\.ytimg\.|^[^"&?/\s=#.:]{11}(?:\?|,|$)/;

  /**
   * @abstract
   * @protected
   */
  get oembedUrl() {
    const videoUrl = `https://www.youtube.com/watch?v=${this.id}`;
    const oembedUrl = 'https://www.youtube.com/oembed';
    const params = new URLSearchParams(Object.entries({...this.options.params, url: videoUrl}));

    return `${oembedUrl}?${params.toString()}`;
  }

  /**
   * Create new Youtube player instance for the given response metadata
   *
   * Uses the youtube-player npm package which automatically:
   * - Loads the YouTube IFrame API
   * - Queues commands until API is ready
   * - Provides a Promise-based interface
   *
   * @protected
   *
   * @param {Metadata} metadata Youtube metadata object
   *
   * @returns {Player} Returns new Youtube player instance
   */
  async createPlayer(metadata) {
    const div = document.createElement('div');

    this.applyClip(div);
    this.element.appendChild(div);

    // Convert boolean params to numbers (1/0) for YouTube API
    const playerVars = {
      ...this.convertParams(this.options.params),

      // Always enable youtube js API
      enablejsapi: 1,
      origin: window.location.origin,

      // Note: The `loop` parameter has limited support in iFrame embeds.
      //       To loop a single video, set the `loop` parameter value to `1` and set the `playlist` parameter value
      //       to the same video ID.
      // See: Player Parameters - https://developers.google.com/youtube/player_parameters#loop
      playlist: this.id,
    };

    // Use youtube-player factory - it handles API loading and queueing
    // Returns a promise-based wrapper that can be used like the native YT.Player
    const ytPlayer = YouTubePlayerFactory(div, {
      videoId: this.id,
      playerVars,
    });

    // Wait for player to be ready
    return new Promise((resolve, reject) => {
      // Set up error handler
      ytPlayer.on('error', (event) => {
        reject(event);

        if (typeof this.options.events.onError === 'function') {
          this.options.events.onError(this, event);
        }
      });

      // Wait for ready event
      ytPlayer.on('ready', (event) => {
        // Only create player adapter once
        if (!this.player) {
          // Use ytPlayer directly - it provides all necessary methods
          const youtubePlayer = new YoutubePlayer(event.target, this.options);

          this.player = new Player(youtubePlayer);

          resolve(this.player);

          if (typeof this.options.events.onReady === 'function') {
            this.options.events.onReady(this, event);
          }
        } else {
          resolve(this.player);

          if (typeof this.options.events.onReady === 'function') {
            this.options.events.onReady(this, event);
          }
        }
      });
    });
  }
}
